import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
} from "reactstrap";

// Pages Components
import WelcomeComp from "./WelcomeComp";
import OperationsPanel from "./OperationsPanel";

//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

//i18n
import { withTranslation } from "react-i18next";

//redux
import { useSelector, useDispatch } from "react-redux";

import {
  getUsers as onGetUsers,
  getNe as onGetNe,
  getCompany as onGetCompany,
  getForum as onGetForum,
} from "store/contacts/actions";

import { ODATA_BASE } from "helpers/api_config";
import { onayBekleyenSayisiniGetir } from "../Contacts/Moderation/moderationTypes";

const IMG_BASE = "https://megasubdomain.stechomeyazilim.info/";

/**
 * Sayi karti.
 *
 * Onceki hali temanin "mini-stats-wid" kartiydi: baslik, deger ve ikon ayni
 * gorsel agirliktaydi, tiklanabilir oldugu da belli olmuyordu. Simdi deger
 * one cikiyor, kart uzerine gelince kalkiyor ve nereye gidecegi ok ile
 * gosteriliyor.
 */
const StatCard = ({ title, value, icon, color, to }) => {
  const govde = (
    <Card>
      <CardBody>
        <div className="d-flex align-items-center">
          <div className="flex-grow-1">
            <p className="bes-sayi-kart__baslik">{title}</p>
            <h4 className="bes-sayi-kart__deger">{value}</h4>
          </div>
          <span className={"bes-sayi-kart__ikon bg-soft-" + color + " text-" + color}>
            <i className={"bx " + icon} />
          </span>
        </div>
        {to ? (
          <div className="mt-2 font-size-11 text-muted">
            Listeye git <i className="bx bx-right-arrow-alt align-middle" />
          </div>
        ) : null}
      </CardBody>
    </Card>
  );

  return to ? (
    <Link to={to} className="bes-sayi-kart">{govde}</Link>
  ) : (
    <div className="bes-sayi-kart">{govde}</div>
  );
};

const Dashboard = props => {
  const dispatch = useDispatch();

  // Ilan sayisi: redux store'da bu liste tutulmuyor, dogrudan OData'dan
  // okunuyor (Moderation ve OperationsPanel ile ayni yaklasim).
  const [ilanSayisi, setIlanSayisi] = useState(null);

  // Karsilama kartindaki "Onay Bekleyenler" rozeti icin; menudeki rozetle
  // ayni kaynaktan (moderationTypes -> ONAY_SORGULARI) geliyor.
  const [onayBekleyen, setOnayBekleyen] = useState(0);

  useEffect(() => {
    let acik = true;
    onayBekleyenSayisiniGetir().then((toplam) => acik && setOnayBekleyen(toplam));
    return () => { acik = false; };
  }, []);

  useEffect(() => {
    let acik = true;
    fetch(`${ODATA_BASE}/Job_Advertisement?$select=ID`, {
      headers: { Accept: "application/json" },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j) => acik && setIlanSayisi(((j && j.value) || []).length))
      .catch(() => acik && setIlanSayisi(null));
    return () => { acik = false; };
  }, []);

  const { users, news, company, forum } = useSelector(state => ({
    users: state.contacts.users,
    news: state.contacts.ne,
    company: state.contacts.company,
    forum: state.contacts.forum,
  }));

  useEffect(() => {
    dispatch(onGetUsers());
    dispatch(onGetNe());
    dispatch(onGetCompany());
    dispatch(onGetForum());
  }, [dispatch]);

  document.title = "Anasayfa | Berlin Esnaf Admin Paneli";

  const safeUsers = Array.isArray(users) ? users : [];
  const safeNews = Array.isArray(news) ? news : [];
  const safeCompany = Array.isArray(company) ? company : [];
  const safeForum = Array.isArray(forum) ? forum : [];

  // Referans istatistikleri
  const referredCount = useMemo(
    () => safeUsers.filter(u => u.Users_Referred_By && String(u.Users_Referred_By).trim() !== "").length,
    [safeUsers]
  );
  const totalPoints = useMemo(
    () => safeUsers.reduce((sum, u) => sum + (u.Users_Points != null ? u.Users_Points : 0), 0),
    [safeUsers]
  );

  // Son 5 üye (ID büyükten küçüğe)
  const latestUsers = useMemo(
    () => [...safeUsers].sort((a, b) => (b.ID || 0) - (a.ID || 0)).slice(0, 5),
    [safeUsers]
  );

  // Son 5 aktüel
  const latestNews = useMemo(
    () => [...safeNews].sort((a, b) => (b.ID || 0) - (a.ID || 0)).slice(0, 5),
    [safeNews]
  );

  const stats = [
    { title: "Toplam Üye", value: safeUsers.length, icon: "bx-user", color: "primary", to: "/users" },
    { title: "Toplam Aktüel", value: safeNews.length, icon: "bx-news", color: "info", to: "/news" },
    { title: "Toplam Firma", value: safeCompany.length, icon: "bx-store", color: "success", to: "/company" },
    { title: "Toplam Forum", value: safeForum.length, icon: "bx-conversation", color: "warning", to: "/forum" },
    // Ilanlar uygulamanin ana bolumlerinden biri ama ana sayfada karti yoktu
    { title: "Toplam İlan", value: ilanSayisi === null ? "—" : ilanSayisi, icon: "bx-briefcase", color: "secondary", to: "/jobadvertisement" },
    { title: "Referansla Gelen", value: referredCount, icon: "bx-gift", color: "danger", to: "/referral-report" },
    { title: "Dağıtılan Puan", value: totalPoints, icon: "bx-star", color: "dark", to: "/referral-report" },
    // Kartlar xl'de 4'lu diziliyor; 8. kart hem izgarayi tamamliyor hem de
    // gunun en onemli isini (onay kuyrugu) ust satirda gorunur kiliyor.
    { title: "Onay Bekleyen", value: onayBekleyen, icon: "bx-shield-quarter", color: "danger", to: "/moderation" },
  ];

  return (
    <React.Fragment>
      <div className="page-content bes-anasayfa">
        <Container fluid>
          <Breadcrumbs title={props.t("Berlin Esnaf")} breadcrumbItem={props.t("Panel Özeti")} />

          <Row>
            <Col xl="3" className="mb-3 mb-xl-0">
              <WelcomeComp onayBekleyen={onayBekleyen} />
            </Col>
            <Col xl="9">
              <Row className="g-3">
                {stats.map((s, key) => (
                  <Col xl="3" md="4" sm="6" key={"stat_" + key}>
                    <StatCard {...s} />
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>

          {/* Yönetim özeti: onay bekleyenler + günlük yeni kayıtlar */}
          <OperationsPanel />

          <Row>
            <Col lg="6">
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center mb-3">
                    <h5 className="card-title flex-grow-1 mb-0">Son Kayıt Olan Üyeler</h5>
                    <Link to="/users" className="btn btn-sm btn-soft-primary">Tümü</Link>
                  </div>
                  <div className="table-responsive">
                    <Table className="table table-centered table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>İsim</th>
                          <th>Telefon</th>
                          <th>Referans Kodu</th>
                          <th>Puan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {latestUsers.length === 0 ? (
                          <tr><td colSpan="4" className="text-muted text-center">Kayıt yok</td></tr>
                        ) : latestUsers.map((u) => (
                          <tr key={u.ID}>
                            <td>{u.Users_Name_Surname || u.Users_Nickname || "-"}</td>
                            <td>{u.Users_PhoneNumber || "-"}</td>
                            <td><span className="badge badge-soft-primary">BES{u.ID}</span></td>
                            <td><span className="badge badge-soft-warning">{u.Users_Points != null ? u.Users_Points : 0}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg="6">
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center mb-3">
                    <h5 className="card-title flex-grow-1 mb-0">Son Eklenen Aktüeller</h5>
                    <Link to="/news" className="btn btn-sm btn-soft-primary">Tümü</Link>
                  </div>
                  <div className="table-responsive">
                    <Table className="table table-centered table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 60 }}>Görsel</th>
                          <th>Başlık</th>
                          <th>Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {latestNews.length === 0 ? (
                          <tr><td colSpan="3" className="text-muted text-center">Kayıt yok</td></tr>
                        ) : latestNews.map((n) => (
                          <tr key={n.ID}>
                            <td>
                              {n.News_Image ? (
                                <img src={IMG_BASE + n.News_Image} alt="" className="avatar-sm rounded" />
                              ) : (
                                <span className="avatar-sm rounded bg-light d-inline-block" />
                              )}
                            </td>
                            <td className="text-truncate" style={{ maxWidth: 220 }}>{n.News_Title}</td>
                            <td>
                              {(n.News_Fixed === true || n.News_Fixed === 1)
                                ? <span className="badge badge-soft-success"><i className="mdi mdi-star me-1" />Öne Çıkan</span>
                                : <span className="text-muted">-</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

Dashboard.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(Dashboard);
